import { useState } from 'react';
import { LexidyMark, AuthHexScene } from '@/components/LexidyLogo';
import { useUser } from '@/auth/AuthContext';
import { KBIcon } from '@/components/kbIcons';

/**
 * Branded full-screen login — one door: the firm's Google account, @lexidy.com only.
 * The server runs the Google flow and sends any failure back as `?auth_error=`,
 * which AuthContext turns into the message shown here.
 * Reuses the onboarding shell classes (.onb*) for visual consistency.
 */
export function Login() {
  const { signInWithGoogle, error } = useUser();
  const [busy, setBusy] = useState(false);

  return (
    <div className="onb">
      <div className="onb-shell">
        {/* LEFT — brand panel */}
        <div className="onb-left">
          <div className="onb-brand">
            <span className="onb-logo"><LexidyMark size={30} className="onb-logo-mark" /> Lexidy</span>
            <span className="onb-tag">
              Borderless
              <br />
              legal care
            </span>
          </div>
          <div className="onb-scene" aria-hidden="true"><AuthHexScene /></div>
          <div className="onb-left-foot">
            <div className="onb-eyebrow">Agent Portal · © 2026 Lexidy</div>
          </div>
        </div>

        {/* RIGHT — auth */}
        <div className="onb-right" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="onb-right-mid">
            <h2 className="onb-q">Sign in</h2>
            <p className="onb-sub">Internal Agent Portal — for the Lexidy team.</p>

            <button type="button" className="auth-google" disabled={busy}
              onClick={() => { setBusy(true); void signInWithGoogle().then((ok) => { if (!ok) setBusy(false); }); }}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            {/* The two pieces of small print live together in one soft
                card, clearly apart from the actions: the door's rule, and
                who to write when the door won't open (a Gmail compose to
                the product team, ready to send). */}
            <div className="auth-hints">
              <span className="auth-hint">
                <KBIcon name="key" size={13} />
                <span>Only <strong>@lexidy.com</strong> accounts can access the portal.</span>
              </span>
              <span className="auth-hint">
                <KBIcon name="mail" size={13} />
                <span>
                  Trouble signing in?{' '}
                  <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=product@lexidy.com&su=Agent%20Portal%20%E2%80%94%20trouble%20signing%20in"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact the product team →
                  </a>
                </span>
              </span>
            </div>

            {error && <p className="auth-err">{error}</p>}
          </div>
          {/* The small print, at the very floor of the page — present, never
              speaking over anything. */}
          <div className="onb-legal">
            © 2026 Lexidy — Borderless legal care. Agent Portal, property of Lexidy. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}
